import { Component, Input, OnInit, ViewChild, Output, EventEmitter, OnChanges, SimpleChanges, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import {
    HisSearchResultComponent, HisAuthService, HisUiService, HisNotificationService, HisConfirmationService,
    HisSearchResultRow, HisNavigationService, HisGlobalDataService, Notification, NotificationType
} from 'hisng';

import { Publication } from 'app/fs/res/publication/model/publication';
import { PublicationService } from 'app/fs/res/publication/service/publication.service';
import { CONFIRMATION_PDIALOG_KEY } from 'app/fs/res/common/component/confirmation-dialogs/confirmation-dialog.component';
import { INFORMATION_PDIALOG_KEY } from 'app/fs/res/common/component/confirmation-dialogs/information-dialog.component';
import { NOTIFICATION } from 'app/fs/res/publication/model/constants';
import { PaginatorState } from 'primeng/paginator';


@Component( {
    selector: 'res-publication-duplicates-search-result-list',
    templateUrl: './publication-duplicates-search-result-list.component.html'
} )
export class PublicationDuplicatesSearchResultListComponent implements OnInit, OnChanges {

    @Input() compName: string;

    @ViewChild( HisSearchResultComponent, { static: true } ) searchResultListComponent: HisSearchResultComponent;

    @Input() data: HisSearchResultRow[];

    @Input() totalRecords: number;
    @Input() rowsPerPage: number;
    @Input() first: number;

    @Input() prevPage: string; // 'pubSearch' or 'importCandidates' if we display duplicates; undefined otherwise

    // die id der "Haupt-Publikation", die in der Überschrift dieser Liste angezeigt werden soll
    // (beim Publikationsimport ist dies z.B. der Importkandidat, dessen Duplikate gezeigt werden sollen)
    @Input() mainPublicationId: number;
    // und die dazugehörige HisSearchResultRow
    public mainRow: HisSearchResultRow;

    @Output() page = new EventEmitter();

    public currentPage: number;
    public totalPage: number;

    constructor(
        public auth: HisAuthService,
        private confirmationService: HisConfirmationService,
        public publicationService: PublicationService, // not private because used in template
        public hisSearchResultComponent: HisSearchResultComponent,
        private hisUiService: HisUiService,
        private notificationService: HisNotificationService,
        private navigationService: HisNavigationService,
        private globalDataService: HisGlobalDataService,
        private route: ActivatedRoute
    ) { }

    ngOnInit() {
        let params = this.route.snapshot.queryParams;
	    let page = Number(params.page);
	    if (page) {
	        this.first = ( page - 1 ) * this.rowsPerPage;
	    }

	    this.page.emit( { first: this.first, rows: this.rowsPerPage } );
    }

    ngOnChanges( changes: SimpleChanges ) {
        if ( changes.data ) { // changes.data.currentValue is equal to @Input() data
            console.log( 'publication search result list: rows changed to', changes.data.currentValue );

            // load additional attributes from backend
            let publicationIds: number[] = this.data.map( searchResultRow => parseInt( searchResultRow.get('Publication.id'), 10 ) );
            console.log('search result publicationIds =', publicationIds);
            if ( publicationIds.length > 0 ) {
                this.publicationService.getPublicationsIncludingOwnImportsByIds( publicationIds )
                    .subscribe( (publications: Publication[]) => {
                        console.log('reloaded publications =', publications);
                        for ( let i = 0; i < this.data.length; i++ ) {
                            this.enrichSearchResultRowFromPublication(this.data[i], publications[i]);
                        }
                    } );
            }

            console.log('mainPublicationId =', this.mainPublicationId);
            if ( this.mainPublicationId ) {
                // TODO eigenen REST-Service für einzelne Publikation implementieren ?
                this.publicationService.getPublicationsIncludingOwnImportsByIds( [this.mainPublicationId] )
                    .subscribe( (publications: Publication[]) => {
                        console.log('reloaded main publication(s) =', publications);
                        if ( publications.length > 0 ) {
                            let mainPublication = publications[0];
                            this.mainRow = new HisSearchResultRow([], []);
                            this.mainRow.set('Publication.id', mainPublication.id);
                            this.mainRow.set('Publication.title', mainPublication.title);
                            this.mainRow.set('Publication.releaseYear', mainPublication.releaseYear);
                            this.mainRow.set('Publication.publicationType.defaulttext', mainPublication.publicationType.defaulttext);
                            this.mainRow.set('Publication.status.defaulttext', mainPublication.status.defaulttext);

                            // mainRow braucht auch canEdit und canDelete flags.
                            // canEditPublication() und hasRightToDeletePublication() lassen auch eigene Importkandidaten durch...
                            this.publicationService.canEditPublication(this.mainPublicationId).subscribe( canEdit => {
                                this.mainRow.set('canEdit', canEdit);
                            } );
                            this.publicationService.hasRightToDeletePublication(this.mainPublicationId).subscribe( canDelete => {
                                this.mainRow.set('canDelete', canDelete);
                            } );

                            this.enrichSearchResultRowFromPublication(this.mainRow, mainPublication);
                        }
                    } );
            }

            this.totalPage = ( ( this.totalRecords - ( this.totalRecords % this.rowsPerPage ) ) / this.rowsPerPage ) + 1;
            this.currentPage = ( this.first / this.rowsPerPage ) + 1;
        }
    }

    private enrichSearchResultRowFromPublication(searchResultRow: HisSearchResultRow, pub: Publication) {
        // add former hiddenSelectColumns
        searchResultRow.set( 'Publication.qualificationThesis.longtext', pub.qualificationThesis.defaulttext );
        searchResultRow.set( 'Publication.qualificationThesis.hiskeyId', pub.qualificationThesis.hiskeyId );
        searchResultRow.set( 'Publication.publicationCreatorType.hiskeyId', pub.publicationCreatorType.hiskeyId );
        console.log('peerReviewedProcess =', pub.peerReviewedProcess );
        let peerReviewed: boolean = pub.peerReviewedProcess && pub.peerReviewedProcess.id === 1;
        searchResultRow.set( 'Publication.peerReviewed', peerReviewed );
        searchResultRow.set( 'Publication.access.hiskeyId', pub.access.hiskeyId );

        // set all publication creators
        let creatorsList: string[] = [];
        for ( let creator of pub.creators ) {
            let creatorStr = creator.firstname ? creator.firstname + ' ' + creator.creatorname : creator.creatorname;
            creatorsList.push( creatorStr );
        }

        searchResultRow.set( 'creatorNamesStr', creatorsList.join( ', ') );
        searchResultRow.set( 'creatorNamesList', creatorsList );

        let globalIdentifiers: string[] = pub.globalIdentifiers.map(gi => gi.globalIdentifierType.uniquename + ': ' + gi.identifier);
        searchResultRow.set( 'Publication.globalIdentifiers', globalIdentifiers.join(', ') );
    }

    /**
     * Page change function of PrimeNG
     *
     * @param event.pageCount Total number of pages
     * @param event.page  Index of the new page
     * @param event.first Index of the first record
     */
    paginate( event: PaginatorState ) {
        this.page.emit( event );
    }

    /**
     * delete the given publication from database.
     *
     * @param publicationRow the publication to delete
     */
    onDelete( publicationRow: HisSearchResultRow ) {
        console.log('onDelete publicationRow =', publicationRow);
        let publicationId = publicationRow.get( 'Publication.id' );
        this.publicationService.canDeletePublication( publicationId ).subscribe( (canDelete: string) => {
            if ( canDelete === '' ) {
                this.confirm( 'fs.res.publication.confirmDeletePublication', publicationRow, canDelete);
            } else {
                this.confirm( canDelete, publicationRow, canDelete);
            }
        } );
    }

    /**
     * confirmation/information dialog to delete publication
     *
     * @param msg dialog message
     * @param publicationRow (object) to delete
     * @param canDelete identifier publication can be deleted
     */
    private confirm( msg: string, publicationRow: HisSearchResultRow, canDelete: string ) {
        let dialogKey = canDelete === '' ? CONFIRMATION_PDIALOG_KEY : INFORMATION_PDIALOG_KEY;
        let title = publicationRow.get( 'Publication.title' );
        let translatedMsg = canDelete === '' ? this.hisUiService.translate( msg, title ) : canDelete;
        let publicationId = publicationRow.get( 'Publication.id' );
        this.confirmationService.confirm( {
            key: dialogKey,
            message: translatedMsg,
            accept: () => {
                if ( canDelete === '' ) {
                    this.publicationService.deletePublication( publicationId ).subscribe( (deleteSuccess: number) => {
                        if ( deleteSuccess === 1 ) {
                            if (publicationId === this.mainPublicationId) {
                                // Nach Löschen der Hauptpublikation wollen wir zurück zur vorherigen Seite (z.B. Importkandidaten).
                                // Damit auch da eine Löschbestätigung angezeigt wird, müssen wir diese in den globalDataService stecken.
                                //
                                // Wir könnten hier auch DELETE_SUCCESS_MESSAGE nehmen wie in finance/model/constants.ts,
                                // aber die allgemeine Form ist leichter erweiterbar, z.B. in Bezug auf Barrierefreiheit.
                                let notification: Notification = {
                                    type: NotificationType.Success,
                                    header: this.hisUiService.translate('fs.res.common.confirmation.delete'),
                                    messages: [this.hisUiService.translate('view.success.delete')],
                                    state: 'new',
                                    timeOut: 4000
                                };
                                this.globalDataService.setData(NOTIFICATION, notification);
                                this.back();
                            } else {
                                // Beim Löschen ähnlicher Publikationen laden wir bloss die Duplikatsseite neu
                                this.notificationService.deleteSuccessI18nRes();
                                this.hisSearchResultComponent.reloadPageAfterDelete();
                            }
                        } else {
                            console.log('delete failed with result =', deleteSuccess);
                        }
                    } );
                } else {
                    console.log('delete not allowed');
                }
            }
        } );
    }

    back() {
        this.navigationService.navigateToLastBackUrl();
    }
}
